import { useState } from 'react';
import { Decimal } from 'decimal.js';
import { numberUtils, ValueDisplay } from '@zen/common-utils';

const { toValue, isZero, toEditDisplay, truncateToDecimalPlaces } = numberUtils;
const CALC_PRECISION = 18; // a little less than the decimal precision to get very small rounding
const MAX_DECIMAL_PLACES = 20;

/**
 * Contains the logic of the price, quantity and total form
 *
 * @param {string} initialPrice
 * @param {string} upToQuantity - maximum quantity allowed
 */
export function usePriceQuantityTotal(initialPrice = '', upToQuantity) {
  // initial price should be displayed truncated
  const [_price, _setPrice] = useState(
    ValueDisplay.create(initialPrice, toEditDisplay(initialPrice))
  );
  const [_quantity, _setQuantity] = useState(ValueDisplay.create());
  const [_total, _setTotal] = useState(ValueDisplay.create());
  const [limitError, setLimitError] = useState(false);

  /**
   * Change the total or quantity when price changes
   */
  const handlePriceChange = (display) => {
    const calculatedPrice = _price.onDisplayChange(
      truncateToDecimalPlaces(display, MAX_DECIMAL_PLACES)
    );
    _setPrice(calculatedPrice);

    let calculatedQuantity = _quantity;

    // calculate quantity before total if it is zero
    if (isZero(_quantity.value) && !isZero(_total.value)) {
      // calculate the quantity by total and then limit it by upToQuantity
      calculatedQuantity = limitQuantity(
        upToQuantity,
        ValueDisplay.create(
          getQuantityByTotal({
            price: calculatedPrice,
            quantity: _quantity,
            total: _total,
          })
        )
      );

      _setQuantity(calculatedQuantity);
    }

    // if quantity exists, calculate total
    if (!isZero(calculatedQuantity.value)) {
      _setTotal(
        ValueDisplay.create(
          getTotalByQuantity({
            price: calculatedPrice,
            quantity: calculatedQuantity,
            total: _total,
          })
        )
      );
    }
  };

  /**
   * Change the total when quantity changes
   * Limit the quantity by upToQuantity
   */
  const handleQuantityChange = (display) => {
    const quantity = _quantity.onDisplayChange(
      truncateToDecimalPlaces(display, MAX_DECIMAL_PLACES)
    );
    const total = ValueDisplay.create(
      getTotalByQuantity({ price: _price, quantity, total: _total })
    );
    setQuantityAndTotal({ quantity, total });
  };

  /**
   * Change the quantity when total changes
   */
  const handleTotalChange = (display) => {
    const total = _total.onDisplayChange(truncateToDecimalPlaces(display, MAX_DECIMAL_PLACES));
    const quantity = ValueDisplay.create(
      getQuantityByTotal({ price: _price, quantity: _quantity, total })
    );
    setQuantityAndTotal({ quantity, total });
  };

  const setQuantityAndTotal = ({ quantity, total }) => {
    let limitError = false;
    if (upToQuantity && new Decimal(quantity.safeValue).greaterThan(upToQuantity)) {
      limitError = true;
    }
    if (limitError) {
      _setQuantity(_quantity);
      _setTotal(_total);
      setLimitError(true);
    } else {
      _setQuantity(quantity);
      _setTotal(total);
      setLimitError(false);

      // if and only if price does not exist, calculate it
      if (isZero(_price.value)) {
        _setPrice(
          ValueDisplay.create(
            getPriceByQuantityAndTotal({
              price: _price,
              quantity: quantity,
              total,
            })
          )
        );
      }
    }
  };

  return {
    price: _price,
    quantity: _quantity,
    total: _total,
    limitError,
    handlePriceChange,
    handleQuantityChange,
    handleTotalChange,
  };
}

/**
 * @param {{price: ValueDisplay, quantity: ValueDisplay, total: ValueDisplay}} { price, quantity, total } - the values
 * @returns {string} the total value
 */
const getTotalByQuantity = ({ price, quantity, total }) => {
  return toValue(
    !isZero(price.safeValue)
      ? new Decimal(quantity.safeValue).times(price.safeValue).toDecimalPlaces(CALC_PRECISION)
      : new Decimal(total.safeValue)
  );
};
/**
 * @param {{price: ValueDisplay, quantity: ValueDisplay, total: ValueDisplay}} { price, quantity, total } - the values
 * @returns {string} the quantity value
 */
const getQuantityByTotal = ({ price, quantity, total }) => {
  return toValue(
    !isZero(price.safeValue)
      ? new Decimal(total.safeValue).div(price.safeValue).toDecimalPlaces(CALC_PRECISION)
      : new Decimal(quantity.safeValue)
  );
};
/**
 * @param {{price: ValueDisplay, quantity: ValueDisplay, total: ValueDisplay}} { price, quantity, total } - the values
 * @returns {string} the price value
 */
const getPriceByQuantityAndTotal = ({ price, quantity, total }) => {
  return toValue(
    !isZero(quantity.safeValue) && !isZero(total.safeValue)
      ? new Decimal(total.safeValue).div(quantity.safeValue).toDecimalPlaces(CALC_PRECISION)
      : new Decimal(price.safeValue)
  );
};
const limitQuantity = (limit, quantity) => {
  return limit && new Decimal(quantity.value).greaterThan(limit)
    ? ValueDisplay.create(String(limit))
    : quantity;
};
